// TaskModal — MUI Dialog for creating/editing tasks, replaces separate route pages.
// Controlled via URL search params: ?new-task or ?task=:taskId
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Dialog, DialogContent } from '@mui/material';
import { DndContext } from '@dnd-kit/core';
import { useT } from '@/i18n/useT';
import { prepareTaskData } from '@/features/tasks/components/taskModalData';
import TaskForm from '@/features/tasks/components/forms/TaskForm';
import { useBreakpoint, isPhoneBreakpoint } from '@/hooks/ui/useBreakpoint.js';
import useAddTask from '@/features/tasks/hooks/mutations/use-task-add';
import useTaskQueries from '@/features/tasks/api/use-task-queries.js';
import { useAuth } from '@/features/auth/useAuth';
import { useTasksContext } from '@/features/tasks/context/TasksContext.def';
import { useTaskMutationsContext } from '@/features/tasks/context/useTaskMutationsContext';
import { TaskModalHeader } from '@/features/tasks/components/TaskModalHeader';
import {
  normalizeTaskDateField,
  submitTaskModalForm,
} from '@/features/tasks/components/taskModalHelpers';

const TaskModal = () => {
  const t = useT();
  const navigate = useNavigate();
  const location = useLocation();
  const currentBreakpoint = useBreakpoint();
  const isSmallScreen = isPhoneBreakpoint(currentBreakpoint);

  const { user } = useAuth();
  const { addTask } = useAddTask();
  // updateTask (niet updateTaskDetails) zodat project/status naar descendants propageert
  const { updateTask } = useTaskMutationsContext();
  const { allTasks } = useTasksContext();
  const { fetchTaskById } = useTaskQueries();

  const params = new URLSearchParams(location.search);
  const taskId = params.get('task');
  const isNewTask = params.has('new-task');
  const isOpen = isNewTask || !!taskId;

  const [initialTask, setInitialTask] = useState(null);
  const [title, setTitle] = useState('');
  const [isLoading, setIsLoading] = useState(!!taskId);
  const titleInputRef = useRef(/** @type {HTMLInputElement|null} */ (null));

  const handleClose = useCallback(() => {
    const basePath = location.pathname;
    navigate(basePath, { replace: true });
  }, [navigate, location.pathname]);

  useEffect(() => {
    if (!taskId || !user) {
      setInitialTask(null);
      setTitle('');
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    let cancelled = false;
    const fetchTask = async () => {
      try {
        const taskData = await fetchTaskById(taskId, user.uid);
        if (cancelled) return;
        if (taskData) {
          normalizeTaskDateField(taskData);
          setInitialTask(taskData);
          setTitle(taskData.title || '');
        } else {
          console.warn(`Task with ID ${taskId} not found.`);
          handleClose();
        }
      } catch (error) {
        console.error('Failed to fetch task:', error);
        handleClose();
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchTask();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId, user, fetchTaskById]);

  useEffect(() => {
    if (isNewTask) {
      setInitialTask(null);
      setTitle('');
    }
  }, [isNewTask]);

  const handleSubmit = async (taskDataFromForm) => {
    if (!user) {
      console.error('No user is signed in');
      return false;
    }

    // Prefer live input value — avoids stale React state if Save is clicked
    // in the same tick as the last keystroke / Playwright fill.
    const liveTitle =
      titleInputRef.current?.value?.trim() ||
      title ||
      taskDataFromForm?.title ||
      '';

    try {
      await submitTaskModalForm({
        taskDataFromForm,
        taskId,
        title: liveTitle,
        initialTask,
        allTasks,
        addTask,
        updateTaskDetails: updateTask,
        prepareTaskData,
        userUid: user.uid,
      });
      handleClose();
      return true;
    } catch (error) {
      console.error('Firestore operation failed:', error);
      return false;
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <Dialog
      open={isOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{
        sx: {
          borderRadius: isSmallScreen ? 0 : 2,
          ...(isSmallScreen
            ? { display: 'flex', flexDirection: 'column' }
            : {}),
        },
      }}
    >
      <TaskModalHeader
        title={title}
        onTitleChange={setTitle}
        titleInputRef={titleInputRef}
        onClose={handleClose}
      />

      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          pt: 0.5,
          px: 2,
          ...(isSmallScreen
            ? { flex: 1, minHeight: 0, overflow: 'hidden', pb: 0 }
            : {}),
        }}
      >
        {isLoading ? (
          <p>{t('common.loading', 'Loading...')}</p>
        ) : (
          <DndContext>
            <TaskForm
              initialTask={initialTask || undefined}
              handleSubmit={handleSubmit}
              onClose={handleClose}
              tasks={allTasks}
              onUpdate={updateTask}
              titleInHeader
              headerTitle={title}
              onHeaderTitleChange={setTitle}
            />
          </DndContext>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TaskModal;
